import type { FC, ReactNode } from 'react'
import type { LayoutData } from '@/types/content'

import Link from 'next/link'
import { ArrowUpRight, Mail } from 'lucide-react'
import { cms } from '@/lib/cms'
import { MoonPhase } from '@/components/media/moon-phase'
import { Wordmark } from '@/components/media/wordmark'
import { socialEntries } from '@/components/site/social-links'
import { exploreNav, mainNav } from '@/constants/site'

interface FooterProps {
    layout: LayoutData
    hideQuote?: boolean
}

export const Footer: FC<FooterProps> = ({ layout, hideQuote = false }): ReactNode => {
    const { artist, settings } = layout
    const socials = socialEntries(artist)

    return (
        <footer className='relative overflow-hidden border-t border-white/[0.06] bg-obsidian'>
            <div className='pointer-events-none absolute inset-x-0 top-0 h-px hairline-gold' aria-hidden='true' />

            {!hideQuote && settings.footer_quote && (
                <div className='shell relative py-24 text-center sm:py-32'>
                    <div className='mx-auto mb-10 flex items-center justify-center gap-3 text-ivory/30' aria-hidden='true'>
                        {[0.12, 0.3, 0.5, 0.7, 0.88].map(phase => (
                            <MoonPhase key={phase} phase={phase} size={14} />
                        ))}
                    </div>

                    <blockquote className='reveal mx-auto max-w-3xl'>
                        <p className='font-heading text-3xl font-light italic leading-snug text-ivory/90 sm:text-4xl lg:text-5xl'>
                            <span {...cms('site_settings', settings.id, 'footer_quote')}>{settings.footer_quote}</span>
                        </p>
                    </blockquote>

                    {artist.signature && (
                        <p className='mt-8 font-script text-4xl text-gold/80 sm:text-5xl'>
                            <span {...cms('artist', artist.id, 'signature')}>{artist.signature}</span>
                        </p>
                    )}
                </div>
            )}

            <div className='shell relative grid gap-12 border-t border-white/[0.06] py-16 md:grid-cols-12'>
                <div className='md:col-span-4'>
                    <Link href='/' aria-label='Serene Corpse, home' className='inline-block'>
                        <Wordmark />
                    </Link>

                    {artist.tagline && (
                        <p className='mt-5 max-w-xs font-heading text-xl italic text-ivory/60'>
                            <span {...cms('artist', artist.id, 'tagline')}>{artist.tagline}</span>
                        </p>
                    )}

                    {settings.hero_statement && (
                        <p className='mt-4 max-w-xs text-sm leading-relaxed text-ivory/45'>
                            <span {...cms('site_settings', settings.id, 'hero_statement')}>{settings.hero_statement}</span>
                        </p>
                    )}
                </div>

                <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 md:col-span-8'>
                    <div>
                        <p className='eyebrow mb-5 text-ivory/40'>
                            Explore
                        </p>

                        <ul className='space-y-3'>
                            {mainNav.slice(1).map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='text-sm text-ivory/65 transition hover:text-ivory'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className='eyebrow mb-5 text-ivory/40'>
                            Discover
                        </p>

                        <ul className='space-y-3'>
                            {exploreNav.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='text-sm text-ivory/65 transition hover:text-ivory'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='col-span-2 sm:col-span-1'>
                        <p className='eyebrow mb-5 text-ivory/40'>
                            Follow & contact
                        </p>

                        <ul className='space-y-3'>
                            {socials.map(({ label, href, Icon }) => (
                                <li key={label}>
                                    <a href={href} target='_blank' rel='noopener noreferrer' className='group inline-flex items-center gap-2.5 text-sm text-ivory/65 transition hover:text-ivory'>
                                        <Icon className='size-4' />
                                        {label}
                                        <ArrowUpRight className='size-3 opacity-0 transition group-hover:opacity-100' aria-hidden='true' />
                                    </a>
                                </li>
                            ))}

                            {artist.contact_email && (
                                <li>
                                    <a href={`mailto:${artist.contact_email}`} className='inline-flex items-center gap-2.5 text-sm text-ivory/65 transition hover:text-ivory'>
                                        <Mail className='size-4' aria-hidden='true' />
                                        <span {...cms('artist', artist.id, 'contact_email')}>{artist.contact_email}</span>
                                    </a>
                                </li>
                            )}

                            <li>
                                <Link href='/contact' className='inline-flex items-center gap-2.5 text-sm text-ivory/65 transition hover:text-ivory'>
                                    <Mail className='size-4' aria-hidden='true' />
                                    Send a message
                                </Link>
                            </li>
                        </ul>
                    </div>
                </nav>
            </div>

            <div className='shell relative flex flex-col gap-4 border-t border-white/[0.06] py-8 text-xs text-ivory/40 sm:flex-row sm:items-center sm:justify-between'>
                <p>
                    &copy; {layout.year} <span {...cms('artist', artist.id, 'name')}>{artist.name}</span>. All rights reserved.
                </p>

                <div className='flex items-center gap-5'>
                    <Link href='/privacy' className='transition hover:text-ivory'>
                        Privacy
                    </Link>

                    <Link href='/admin' className='transition hover:text-ivory'>
                        Artist login
                    </Link>
                </div>
            </div>
        </footer>
    )
}
